import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import {
  FaIconComponent,
  FaIconLibrary,
} from '@fortawesome/angular-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { selectUser } from '@core/states/auth/auth.selectors';
import { AvatarComponent } from '@group24ag/flowkit';
import { NgClass } from '@angular/common';
import { AssetService } from '@shared/api/asset/asset.service';
import { provideAssetService } from '@shared/api/asset/provide-asset-service';

@Component({
  selector: 'app-sidebar-user-menu-trigger',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FaIconComponent, AvatarComponent, NgClass],
  providers: [provideAssetService()],
  template: `
    <flk-avatar
      class="mt-2 w-full! rounded-xl p-1.5"
      [ngClass]="containerClass"
      [size]="'lg'"
      [src]="avatarUrl()"
      [initials]="initials">
      @if (!collapsed()) {
        <div class="flex w-full justify-between overflow-hidden">
          <div class="flex w-full min-w-0 flex-col justify-center pb-[3px]">
            <span class="text--contrast-high truncate text-sm font-medium">
              {{ user()?.firstName }} {{ user()?.lastName }}
            </span>
            <span class="text--contrast-low truncate text-xs">
              {{ user()?.email }}
            </span>
          </div>

          <fa-icon
            class="w-min self-center pr-1"
            [icon]="['fas', 'ellipsis-vertical']" />
        </div>
      }
    </flk-avatar>
  `,
})
export class SidebarUserMenuTriggerComponent {
  /** INJECTIONS **/
  /****/
  private readonly store = inject(Store);

  /****/
  private readonly assetService = inject(AssetService);

  /** INPUTS **/
  /****/
  readonly collapsed = input.required<boolean>();

  /****/
  readonly menuOpen = input<boolean>(false);

  /** PROPERTIES **/
  /****/
  protected readonly user = this.store.selectSignal(selectUser);

  /**
   * The profile picture of the signed-in user for the active theme. Undefined while none is set,
   * so the avatar falls back to the initials rather than to an asset placeholder.
   **/
  protected readonly avatarUrl = computed(() => {
    const user = this.user();
    if (!user?.asset) return undefined;

    return this.assetService.getAssetUrlOrPlaceholder(user, 'user')();
  });

  protected get initials() {
    if (!this.user()) return '';

    const firstName = this.user()?.firstName;
    const lastName = this.user()?.lastName;
    return `${firstName?.charAt(0)}${lastName?.charAt(0)}`;
  }

  protected get containerClass() {
    return {
      'hover:bg-dune-100/40 dark:hover:bg-dune-1000/30': !this.menuOpen(),
      'bg-dune-100/40 dark:bg-dune-1000/30': this.menuOpen(),
    };
  }

  constructor(library: FaIconLibrary) {
    library.addIcons(faEllipsisVertical);
  }
}
